import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import * as Yup from 'yup';
import ProductService from '~/src/services/logic/ProductService';
import { DATE_FORMAT, ROLES } from '~/src/utils/constants';
import { LocationWithQuantitiesDto, PalletDescriptionEditDto } from '../PalletDescriptions';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import { useFormik } from 'formik';
import PalletDescriptionsApi from '~/src/services/api/PalletDescriptionsApi';
import { StatusCodes } from 'http-status-codes';
import { Link, useHistory } from 'react-router-dom';
import InventoryApi from '~/src/services/api/InventoryApi';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import RolesDependent from '~/src/components/common/RolesDependent';
import { Colors } from '~/src/scss/common/style';

interface CellProps {
  disabled?: boolean;
  align?: 'center' | 'left' | 'right';
  width?: string;
}

interface Props {
  item: LocationWithQuantitiesDto;
  index: number;
}

export default function PalletDescriptionRowShort({ item, index }: Props): JSX.Element {
  const history = useHistory();
  const [removeLoading, setRemoveLoading] = React.useState(false);
  const [removed, setRemoved] = React.useState(false);

  const totalQuantities = item.groupedQuantities.reduce((sum, curr) => {
    const quantitiesCu = curr.quantities.reduce((sum, curr) => {
      return sum + (curr.totalCuUnits ?? 0);
    }, 0);

    const du = ProductService.getDu(curr.product.packagedProducts);

    const quantities = Math.ceil(quantitiesCu / (du?.numberSmallerUnits ?? 1));

    return sum + quantities;
  }, 0);

  const updateSortOrderNumber = async (sortOrderNumber: number): Promise<void> => {
    const vals: PalletDescriptionEditDto = {
      orderId: item.palletDescriptionObject.orderId,
      sortOrderNumber: sortOrderNumber,
      name: item.palletDescriptionObject.name,
      locationId: item.palletDescriptionObject.locationId,
      palletId: item.palletDescriptionObject.palletId,
      warehouseId: item.palletDescriptionObject.warehouseId,
    };

    const result = await PalletDescriptionsApi.change(item.palletDescriptionObject.id, vals);
    if (result.status === StatusCodes.OK) {
      if (result.data.message !== '') {
        alert(result.data.message);
        history.go(0);
      }
    } else {
      formik.setFieldValue('sortOrderNumber', '');
    }
  };

  const formik = useFormik({
    initialValues: {
      sortOrderNumber: item.palletDescriptionObject.sortOrderNumber ?? '',
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      sortOrderNumber: Yup.number().typeError('Must be a number').required('Required'),
    }),
    onSubmit: (values) => {
      updateSortOrderNumber(values.sortOrderNumber);
    },
  });

  const removeLocation = async (): Promise<void> => {
    if (!formik.values.sortOrderNumber) {
      formik.setFieldError('sortOrderNumber', 'Required');
      formik.setTouched({ sortOrderNumber: true });

      return;
    }

    setRemoveLoading(true);
    const result = await InventoryApi.removeFromLocationForOrder(item.palletDescriptionObject.id, item.location);
    if (result.status === StatusCodes.OK) {
      if (result.data.result === true && result.data.message === '') {
        setRemoved(true);
      }
    }
    setRemoveLoading(false);
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 50 }}>
        <CellRow className="col-sm-12">
          <FullWidthTable>
            <TableWrapper>
              <thead>
                <tr>
                  <RolesDependent
                    roles={[
                      ROLES.ADMIN,
                      ROLES.SUPER_ADMIN,
                      ROLES.WAREHOUSE_ADMIN,
                      ROLES.WAREHOUSE,
                      ROLES.OFFICE,
                      ROLES.SALES,
                      ROLES.DATABASE_ADMIN,
                      ROLES.SVILA_CLIENT_ADMIN,
                      ROLES.SVILA_CLIENT,
                    ]}
                  >
                    <th>
                      {' '}
                      {!removed ? (
                        <SecondaryButton
                          isBig
                          title="Load"
                          onClick={removeLocation}
                          isSubmitting={removeLoading}
                        />
                      ) : (
                        'Loaded'
                      )}
                    </th>
                  </RolesDependent>
                  <th>Product Description</th>
                  <th>Quantity</th>
                  <th>Layers</th>
                  <th>Expiry Date</th>
                </tr>
              </thead>
              <TBodyStyles>
                {item.groupedQuantities.map((info) => {
                  const du = ProductService.getDu(info.product.packagedProducts);
                  const layer = ProductService.getLayer(info.product.packagedProducts);

                  const quantitiesCu = info.quantities.reduce((sum, curr) => {
                    return sum + (curr.totalCuUnits ?? 0);
                  }, 0);

                  const quantities = Math.ceil(quantitiesCu / (du?.numberSmallerUnits ?? 1));

                  const layerCount = Math.round(100 * (quantities / (layer?.numberSmallerUnits ?? 1))) / 100;

                  if (quantities === 0) {
                    return null;
                  }

                  return (
                    <tr key={index}>
                      <RolesDependent
                        roles={[
                          ROLES.ADMIN,
                          ROLES.SUPER_ADMIN,
                          ROLES.WAREHOUSE_ADMIN,
                          ROLES.WAREHOUSE,
                          ROLES.OFFICE,
                          ROLES.SALES,
                          ROLES.DATABASE_ADMIN,
                          ROLES.SVILA_CLIENT_ADMIN,
                          ROLES.SVILA_CLIENT,
                        ]}
                      >
                        <Cell>
                          <div className="col-sm-12">
                            <StyledLink
                              to={{
                                pathname: `/inventory/move`,
                                search: `?chamberNumber=${item.location.chamberNumber}&column=${item.location.column}&letter=${item.location.letter}&level=${item.location.level}`,
                              }}
                              target="_blank"
                            >
                              {`Pallet Number for location ${item.location.chamberNumber}-${item.location.column}-${item.location.letter}-${item.location.level} in warehouse ${item.palletDescriptionObject?.warehouse?.name}:`}
                            </StyledLink>
                            <form onSubmit={formik.handleSubmit}>
                              <TextInputComponent
                                name="sortOrderNumber"
                                errors={formik.errors}
                                value={formik.values.sortOrderNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleSubmit}
                                cols={12}
                                height="33"
                              />
                            </form>
                          </div>
                        </Cell>
                      </RolesDependent>
                      <Cell align="left">{info.product.genericProduct.name}</Cell>
                      <Cell width="100">{quantities} Du</Cell>
                      <Cell width="100">{layerCount > 0 ? layerCount : '-'}</Cell>
                      <Cell width="100">
                        {info.quantities[0].expirationDate
                          ? moment(info.quantities[0].expirationDate).format(DATE_FORMAT)
                          : null}
                      </Cell>
                    </tr>
                  );
                })}
              </TBodyStyles>
            </TableWrapper>
          </FullWidthTable>
        </CellRow>
      </div>
    </>
  );
}

const Cell = styled.td<CellProps>`
  padding: 5px;
  white-space: pre-line;
  text-align: ${(props): string => (props.align ? props.align : 'center')};
  opacity: ${(props): number => (props.disabled ? 0.3 : 1)};
  width: ${(props): string => (props.width ? props.width + 'px' : 'auto')};
  &:first:child {
    display: flex;
    flex-direction: column;
  }
`;

const CellRow = styled.div`
  flex-direction: row;
  display: flex;
`;
const TrStyle = styled.tr`
  border-bottom: 1px solid #6e6e6e;
`;

const FullWidthTable = styled.table`
  width: 100%;
`;

const TBodyStyles = styled.tbody`
  ${TrStyle}:nth-child(even) {a
    background: #eee;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${Colors.darkBrown};
  font-weight: bold;
  font-size: 18px;
  &:hover {
    text-decoration: none;
    color: black;
  }
`;
